import React, { FC, useEffect, useState } from "react";
import axios, { AxiosRequestHeaders } from "axios";
import { Formik, Field, Form } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { Container, Row, Col, Card, Button } from "react-bootstrap";
import ConfirmationModal from "../shared/ConfirmationModal";
import NewconfirmationModal from "../shared/NewconfirmationModal";
import AllFailureModal from "../shared/AllFailureModal";
import isEqual from "lodash/isEqual";

const SkillsUpdate: FC = () => {
  const { id } = useParams();
  const skillDataInitialState = {
    skill_id: id,
    skill_name: "",
    skill_category: "",
    skill_tag: "",
    skill_shortname: "",
  };
  const [updateData, setUpdateData] = useState(skillDataInitialState);
  const [modalShow, setModalShow] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [pendingValues, setPendingValues] = useState<any>(null); // Stores the form values waiting for confirmation
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showFailureModal, setShowFailureModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [failureMessage, setFailureMessage] = useState("");
  const headers: AxiosRequestHeaders = {
    "x-access-token": localStorage.getItem("jwttoken") || "",
  };
  const navigate = useNavigate();

  // API call to update skill
  const callSkillsUpdateApi = async (data: any) => {
    const url = process.env.REACT_APP_API_URL + "skills/updateskill";
    try {
      const response = await axios.put(url, data, {headers});
      return { error: response.status !== 200 };
    } catch (error) {
      console.error("Error updating skill:", error);
      return { error: true };
    }
  };

  // API call to fetch skill by ID
  const getSkillByIdApi = async (id: any) => {
    const url = process.env.REACT_APP_API_URL + "skills/showskillbyid/" + id;
    try {
      const response = await axios.get(url);
      return response.data[0];
    } catch (error) {
      console.error("Error fetching skill:", error);
      setModalMessage("Failed to fetch skill details. Please try again.");
      setModalShow(true);
      return skillDataInitialState;
    }
  };

  useEffect(() => {
    (async () => {
      if (id) {
        const data = await getSkillByIdApi(id);
        setUpdateData(data);
      }
    })();
  }, [id]);

  // Handles the confirmation modal's response
  const handleUpdateConfirmation = async (isConfirmed: boolean) => {
    setModalShow(false);
    if (isConfirmed && pendingValues) {
      const { error } = await callSkillsUpdateApi(pendingValues);
      if (!error) {
        setSuccessMessage("Skill updated successfully!");
        setShowSuccessModal(true);
        setTimeout(() => {
          navigate("/skills");
        }, 3000);
      } else {
        setFailureMessage("Failed to update skill. Please try again.");
        setShowFailureModal(true);
      }
    }
  };

  return (
    <div>
      <ConfirmationModal
        show={modalShow}
        modalMessage={modalMessage}
        onHide={() => setModalShow(false)}
        confirmation={handleUpdateConfirmation}
        operationType="Confirm"
      />

      <NewconfirmationModal
        show={showSuccessModal}
        onHide={() => setShowSuccessModal(false)}
        modalMessage={successMessage}
        operationType="OK"
        timeoutDuration={3000}
      />

      <AllFailureModal
        show={showFailureModal}
        onHide={() => setShowFailureModal(false)}
        modalMessage={failureMessage}
        operationType="OK"
        timeoutDuration={3000}
      />

      <Container className="mt-5">
        <Row className="justify-content-center">
          <Col md={8}>
            <Card className="shadow">
              <Card.Header className="bg-info text-white text-center">
                <h4>Update Skill Details</h4>
              </Card.Header>
              <Card.Body>
                <Formik
                  enableReinitialize={true}
                  initialValues={updateData}
                  onSubmit={(values) => {
                    if (isEqual(values, updateData)) {
                      setModalMessage("No changes were made.");
                      setModalShow(true);
                    } else {
                      setPendingValues(values);
                      setModalMessage(
                        "Are you sure you want to update the skill?"
                      );
                      setModalShow(true);
                    }
                  }}
                >
                  {() => (
                    <Form>
                      <Field hidden type="number" name="skill_id" />

                      <Row className="mb-3">
                        <Col md={6}>
                          <label htmlFor="skill_name" className="form-label">
                            Skill Name:
                          </label>
                          <Field
                            name="skill_name"
                            type="text"
                            className="form-control"
                            max="100"
                            required
                          />
                        </Col>

                        <Col md={6}>
                          <label
                            htmlFor="skill_category"
                            className="form-label"
                          >
                            Skill Category:
                          </label>
                          <Field
                            name="skill_category"
                            type="text"
                            className="form-control"
                            required
                          />
                        </Col>
                      </Row>

                      <Row className="mb-3">
                        <Col md={6}>
                          <label htmlFor="skill_tag" className="form-label">
                            Skill Tag:
                          </label>
                          <Field
                            name="skill_tag"
                            type="text"
                            className="form-control"
                            required
                          />
                        </Col>

                        <Col md={6}>
                          <label
                            htmlFor="skill_shortname"
                            className="form-label"
                          >
                            Skill Shortname:
                          </label>
                          <Field
                            name="skill_shortname"
                            type="text"
                            className="form-control"
                            required
                          />
                        </Col>
                      </Row>

                      <Row className="mt-4">
                        <Col className="text-center">
                          <Button type="submit" variant="success">
                            Update
                          </Button>
                          <Button
                            variant="danger"
                            className="mx-2"
                            onClick={() => navigate(-1)}
                          >
                            Go Back
                          </Button>
                        </Col>
                      </Row>
                    </Form>
                  )}
                </Formik>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default SkillsUpdate;
